// ============================================================
// SYNC LAYER - sincronizzazione dati con Supabase
// ============================================================
// Strategia:
//  - Dopo login: pull completo dal cloud -> locale (merge con upsert)
//  - Ogni modifica locale: push al cloud (upsert con user_id)
//  - All'avvio: se loggato, fa un pull per aggiornare eventuali
//    modifiche fatte da altri device
//  - L'app continua a funzionare offline: se il push fallisce,
//    resta in coda e si riprova al prossimo sync
// ============================================================

import { getSupabase } from './supabase';
import { getDatabase } from './db';
import type { Note, PersonalTask, ProfitTask, Reminder, AppUser } from '@/types';

export interface SyncResult {
  ok: boolean;
  message: string;
  counts: { notes: number; personal: number; profit: number; reminders: number };
}

// ------------------------------------------------------------
// PUSH (locale -> cloud): upsert di tutti i record locali
// ------------------------------------------------------------
export async function pushLocalToCloud(user: AppUser): Promise<SyncResult> {
  const client = getSupabase();
  const db = getDatabase();

  const [notes, personal, profit, reminders] = await Promise.all([
    db.getAllNotes(),
    db.getAllPersonalTasks(),
    db.getAllProfitTasks(),
    db.getAllReminders(),
  ]);

  const errors: string[] = [];

  // Notes
  if (notes.length > 0) {
    const payload = notes.map((n) => ({
      id: n.id, user_id: user.id,
      title: n.title, content: n.content, color: n.color,
      tags: n.tags as any,
      is_pinned: n.is_pinned, is_archived: n.is_archived,
      reminder_id: n.reminder_id,
      created_at: n.created_at, updated_at: n.updated_at,
    }));
    const { error } = await client.from('notes').upsert(payload, { onConflict: 'id' });
    if (error) errors.push(`notes: ${error.message}`);
  } else {
    await client.from('notes').delete().eq('user_id', user.id);
  }

  if (personal.length > 0) {
    const payload = personal.map((t) => ({
      id: t.id, user_id: user.id,
      title: t.title, description: t.description, status: t.status, priority: t.priority,
      due_date: t.due_date, tags: t.tags as any, position: t.position,
      created_at: t.created_at, updated_at: t.updated_at,
    }));
    const { error } = await client.from('personal_tasks').upsert(payload, { onConflict: 'id' });
    if (error) errors.push(`personal: ${error.message}`);
  } else {
    await client.from('personal_tasks').delete().eq('user_id', user.id);
  }

  if (profit.length > 0) {
    const payload = profit.map((t) => ({
      id: t.id, user_id: user.id,
      title: t.title, description: t.description, status: t.status, priority: t.priority,
      client_name: t.client_name, expected_revenue: t.expected_revenue,
      actual_revenue: t.actual_revenue, cost: t.cost, currency: t.currency,
      due_date: t.due_date, tags: t.tags as any, position: t.position,
      created_at: t.created_at, updated_at: t.updated_at,
    }));
    const { error } = await client.from('profit_tasks').upsert(payload, { onConflict: 'id' });
    if (error) errors.push(`profit: ${error.message}`);
  } else {
    await client.from('profit_tasks').delete().eq('user_id', user.id);
  }

  if (reminders.length > 0) {
    const payload = reminders.map((r) => ({
      id: r.id, user_id: user.id, note_id: r.note_id,
      title: r.title, body: r.body, time: r.time,
      days_of_week: r.days_of_week as any, at: r.at, enabled: r.enabled,
      created_at: r.created_at, updated_at: r.updated_at,
    }));
    const { error } = await client.from('reminders').upsert(payload, { onConflict: 'id' });
    if (error) errors.push(`reminders: ${error.message}`);
  } else {
    await client.from('reminders').delete().eq('user_id', user.id);
  }

  // Aggiorna sync_meta
  await client.from('sync_meta').upsert({
    user_id: user.id,
    last_sync_at: new Date().toISOString(),
    notes_count: notes.length,
    personal_count: personal.length,
    profit_count: profit.length,
    reminders_count: reminders.length,
  }, { onConflict: 'user_id' });

  if (errors.length > 0) {
    return {
      ok: false,
      message: `Sync parziale. Errori: ${errors.join('; ')}`,
      counts: { notes: notes.length, personal: personal.length, profit: profit.length, reminders: reminders.length },
    };
  }

  return {
    ok: true,
    message: `Sincronizzato: ${notes.length} note, ${personal.length} obiettivi, ${profit.length} profit, ${reminders.length} promemoria.`,
    counts: { notes: notes.length, personal: personal.length, profit: profit.length, reminders: reminders.length },
  };
}

// ------------------------------------------------------------
// PULL (cloud -> locale): scarica i dati e fa merge (upsert locale)
// Strategia merge: per ogni record cloud, se esiste localmente aggiorno
// solo se updated_at cloud > updated_at locale. Records locali non
// presenti nel cloud vengono mantenuti (verranno pushati al prossimo sync).
// ------------------------------------------------------------
export async function pullCloudToLocal(user: AppUser): Promise<SyncResult> {
  const client = getSupabase();
  const db = getDatabase();

  const [notesRes, personalRes, profitRes, remindersRes] = await Promise.all([
    client.from('notes').select('*').eq('user_id', user.id),
    client.from('personal_tasks').select('*').eq('user_id', user.id),
    client.from('profit_tasks').select('*').eq('user_id', user.id),
    client.from('reminders').select('*').eq('user_id', user.id),
  ]);

  if (notesRes.error) throw new Error(`notes: ${notesRes.error.message}`);
  if (personalRes.error) throw new Error(`personal: ${personalRes.error.message}`);
  if (profitRes.error) throw new Error(`profit: ${profitRes.error.message}`);
  if (remindersRes.error) throw new Error(`reminders: ${remindersRes.error.message}`);

  const cloudNotes = (notesRes.data ?? []) as any[];
  const cloudPersonal = (personalRes.data ?? []) as any[];
  const cloudProfit = (profitRes.data ?? []) as any[];
  const cloudReminders = (remindersRes.data ?? []) as any[];

  // Merge note
  const localNotes = await db.getAllNotes();
  const localNotesMap = new Map(localNotes.map((n) => [n.id, n]));
  for (const cn of cloudNotes) {
    const local = localNotesMap.get(cn.id);
    const cloudUpdated = new Date(cn.updated_at).getTime();
    const localUpdated = local ? new Date(local.updated_at).getTime() : 0;
    if (!local || cloudUpdated > localUpdated) {
      const note: Note = {
        id: cn.id, title: cn.title, content: cn.content ?? '', color: cn.color ?? 'default',
        tags: Array.isArray(cn.tags) ? cn.tags : [], is_pinned: !!cn.is_pinned, is_archived: !!cn.is_archived,
        reminder_id: cn.reminder_id ?? null,
        created_at: cn.created_at, updated_at: cn.updated_at,
      };
      await db.upsertNote(note);
    }
  }

  // Merge personal tasks
  const localPersonal = await db.getAllPersonalTasks();
  const localPersonalMap = new Map(localPersonal.map((t) => [t.id, t]));
  for (const ct of cloudPersonal) {
    const local = localPersonalMap.get(ct.id);
    const cloudUpdated = new Date(ct.updated_at).getTime();
    const localUpdated = local ? new Date(local.updated_at).getTime() : 0;
    if (!local || cloudUpdated > localUpdated) {
      const task: PersonalTask = {
        id: ct.id, title: ct.title, description: ct.description ?? '',
        status: ct.status, priority: ct.priority ?? 'medium',
        due_date: ct.due_date ?? null, tags: Array.isArray(ct.tags) ? ct.tags : [],
        position: ct.position ?? 0, created_at: ct.created_at, updated_at: ct.updated_at,
      };
      await db.upsertPersonalTask(task);
    }
  }

  // Merge profit tasks
  const localProfit = await db.getAllProfitTasks();
  const localProfitMap = new Map(localProfit.map((t) => [t.id, t]));
  for (const ct of cloudProfit) {
    const local = localProfitMap.get(ct.id);
    const cloudUpdated = new Date(ct.updated_at).getTime();
    const localUpdated = local ? new Date(local.updated_at).getTime() : 0;
    if (!local || cloudUpdated > localUpdated) {
      const task: ProfitTask = {
        id: ct.id, title: ct.title, description: ct.description ?? '',
        status: ct.status, priority: ct.priority ?? 'medium',
        client_name: ct.client_name ?? '', expected_revenue: Number(ct.expected_revenue ?? 0),
        actual_revenue: Number(ct.actual_revenue ?? 0), cost: Number(ct.cost ?? 0),
        currency: ct.currency ?? 'EUR', due_date: ct.due_date ?? null,
        tags: Array.isArray(ct.tags) ? ct.tags : [], position: ct.position ?? 0,
        created_at: ct.created_at, updated_at: ct.updated_at,
      };
      await db.upsertProfitTask(task);
    }
  }

  // Merge reminders
  const localReminders = await db.getAllReminders();
  const localRemindersMap = new Map(localReminders.map((r) => [r.id, r]));
  for (const cr of cloudReminders) {
    const local = localRemindersMap.get(cr.id);
    const cloudUpdated = new Date(cr.updated_at).getTime();
    const localUpdated = local ? new Date(local.updated_at).getTime() : 0;
    if (!local || cloudUpdated > localUpdated) {
      const r: Reminder = {
        id: cr.id, note_id: cr.note_id ?? null, title: cr.title, body: cr.body ?? '',
        time: cr.time ?? null, days_of_week: Array.isArray(cr.days_of_week) ? cr.days_of_week : [],
        at: cr.at ?? null, enabled: !!cr.enabled,
        created_at: cr.created_at, updated_at: cr.updated_at,
      };
      await db.upsertReminder(r);
    }
  }

  return {
    ok: true,
    message: `Pull completato: ${cloudNotes.length} note, ${cloudPersonal.length} obiettivi, ${cloudProfit.length} profit, ${cloudReminders.length} promemoria.`,
    counts: { notes: cloudNotes.length, personal: cloudPersonal.length, profit: cloudProfit.length, reminders: cloudReminders.length },
  };
}

// ------------------------------------------------------------
// FULL SYNC (push + pull)
// ------------------------------------------------------------
export async function fullSync(user: AppUser): Promise<SyncResult> {
  // Prima push (carica le modifiche locali), poi pull (aggiorna con il cloud)
  const pushRes = await pushLocalToCloud(user);
  if (!pushRes.ok) return pushRes;
  return pullCloudToLocal(user);
}
